/**
 * Command Palette — Cmd+K (Mac) / Ctrl+K (Win/Linux).
 * Navegação rápida + busca global no backend.
 */
import { useQuery } from "@tanstack/react-query";
import { Command } from "cmdk";
import {
  BarChart3,
  Briefcase,
  Building2,
  FileText,
  HardHat,
  LayoutDashboard,
  Loader2,
  Radio,
  Receipt,
  Search,
  Settings,
  Tag,
  UserCircle,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { globalSearch } from "@/lib/api-extras";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/jobs", label: "Chamados", icon: Briefcase },
  { to: "/nerds", label: "Técnicos (Nerds)", icon: HardHat },
  { to: "/clients", label: "Clientes", icon: Building2 },
  { to: "/contracts", label: "Contratos", icon: FileText },
  { to: "/categories", label: "Categorias", icon: Tag },
  { to: "/dispatch", label: "Dispatch", icon: Radio },
  { to: "/invoices", label: "Faturas", icon: Receipt },
  { to: "/users", label: "Usuários", icon: Users },
  { to: "/reports", label: "Relatórios", icon: BarChart3 },
  { to: "/profile", label: "Meu perfil", icon: UserCircle },
  { to: "/settings", label: "Configurações", icon: Settings },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Reset query ao fechar
  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const search = useQuery({
    queryKey: ["global-search", q],
    queryFn: () => globalSearch(q),
    enabled: q.length >= 2,
    staleTime: 5_000,
  });

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  const filteredNav = NAV.filter((n) =>
    !q || n.label.toLowerCase().includes(q.toLowerCase()),
  );

  const hasResults =
    search.data && (
      search.data.jobs.length > 0 ||
      search.data.clients.length > 0 ||
      search.data.nerds.length > 0 ||
      search.data.invoices.length > 0
    );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 overflow-hidden max-w-2xl">
        <Command className="bg-background" shouldFilter={false}>
          <div className="border-b border-slate-200/60 dark:border-white/10 flex items-center gap-2 px-4">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Command.Input
              value={q}
              onValueChange={setQ}
              placeholder="Buscar chamados, clientes, técnicos, faturas..."
              className="flex-1 bg-transparent border-0 py-3.5 text-sm focus:outline-none"
            />
            {search.isFetching && <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />}
          </div>

          <Command.List className="max-h-[500px] overflow-y-auto p-2">
            <Command.Empty className="px-4 py-8 text-center text-sm text-muted-foreground">
              {q.length < 2 ? "Digite ao menos 2 caracteres..." : "Nada encontrado."}
            </Command.Empty>

            {/* Navegação */}
            {filteredNav.length > 0 && (
              <Command.Group
                heading="Navegar"
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
              >
                {filteredNav.map(({ to, label, icon: Icon }) => (
                  <Command.Item
                    key={to}
                    value={`nav:${label}`}
                    onSelect={() => go(to)}
                    className={paletteItem}
                  >
                    <Icon className="h-4 w-4 opacity-70 shrink-0" />
                    <span>{label}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Resultados do backend */}
            {hasResults && (
              <>
                {search.data && search.data.jobs.length > 0 && (
                  <Command.Group
                    heading="Chamados"
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
                  >
                    {search.data.jobs.map((j) => (
                      <Command.Item
                        key={j.id}
                        value={`job:${j.id}`}
                        onSelect={() => go(`/jobs/${j.id}`)}
                        className={paletteItem}
                      >
                        <Briefcase className="h-4 w-4 opacity-70 shrink-0" />
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="font-mono text-xs text-muted-foreground">{j.code}</span>
                          <span className="truncate">{j.title}</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground shrink-0 capitalize">{j.status}</span>
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {search.data && search.data.clients.length > 0 && (
                  <Command.Group
                    heading="Clientes"
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
                  >
                    {search.data.clients.map((c) => (
                      <Command.Item
                        key={c.id}
                        value={`client:${c.id}`}
                        onSelect={() => go(`/clients/${c.id}`)}
                        className={paletteItem}
                      >
                        <Building2 className="h-4 w-4 opacity-70 shrink-0" />
                        <span className="truncate">{c.trade_name}</span>
                        <span className="ml-auto font-mono text-[10px] text-muted-foreground">{c.cnpj}</span>
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {search.data && search.data.nerds.length > 0 && (
                  <Command.Group
                    heading="Técnicos"
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
                  >
                    {search.data.nerds.map((n) => (
                      <Command.Item
                        key={n.id}
                        value={`nerd:${n.id}`}
                        onSelect={() => go(`/nerds/${n.id}`)}
                        className={paletteItem}
                      >
                        <HardHat className="h-4 w-4 opacity-70 shrink-0" />
                        <span className="truncate">{n.legal_name ?? "—"}</span>
                        <span className="ml-auto text-[10px] text-muted-foreground">rank {n.rank_score}</span>
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {search.data && search.data.invoices.length > 0 && (
                  <Command.Group
                    heading="Faturas"
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
                  >
                    {search.data.invoices.map((i) => (
                      <Command.Item
                        key={i.id}
                        value={`invoice:${i.id}`}
                        onSelect={() => go(`/invoices/${i.id}`)}
                        className={paletteItem}
                      >
                        <Receipt className="h-4 w-4 opacity-70 shrink-0" />
                        <span className="font-mono">{i.code}</span>
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}
              </>
            )}
          </Command.List>

          <div className="border-t border-slate-200/60 dark:border-white/10 px-3 py-2 text-[11px] text-muted-foreground flex items-center gap-3">
            <kbd className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 font-mono">↵</kbd>
            <span>selecionar</span>
            <kbd className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 font-mono">↑↓</kbd>
            <span>navegar</span>
            <kbd className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 font-mono">esc</kbd>
            <span>fechar</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

const paletteItem = cn(
  "flex items-center gap-2 rounded-lg px-2 py-2 text-sm cursor-pointer",
  "data-[selected=true]:bg-primary-50 dark:data-[selected=true]:bg-primary-500/10",
  "data-[selected=true]:text-primary-700 dark:data-[selected=true]:text-primary-300",
);
